<template>
  <div
    id="SurveyContainer"
  >
    <v-img
      v-if="showBranding()"
      :src="getBrandingImage()"
      class="survey-logo"
    />
    <v-img
      v-if="selectedSurvey.name === '220138-2 220635-2and-4 SR 20'"
      class="logo-sr20 mobile-logo"
      max-height="20em"
      max-width="20em"
      alt="SR-20 logo"
      src="/static/District 3/sr20.png"
    />
    <v-img
      v-if="selectedSurvey.name === '441038-1 413062-4and-5 I-10 Santa Rosa and Okaloosa'"
      class="logo-sr20 mobile-logo"
      max-height="20em"
      max-width="20em"
      alt="I-10 logo"
      src="/static/District 3/i10.png"
    />
    <v-img
      v-if="selectedSurvey.name === '220138-2 220635-2and-4 SR 20' || '441038-1 413062-4and-5 I-10 Santa Rosa and Okaloosa'"
      class="logo-fdot hidden-logo"
      max-height="20em"
      max-width="20em"
      alt="FDOT logo"
      src="/static/District 3/AdminPanelNav.png"
    />
    <div
      class="card survey-card mobile-card"
    >
      <!-- Exhibits -->
      <template v-if="!hasCompleted && exhibits.length !== 0">
        <br>
        <h4 class="text-center">
          Click On These For More Information
        </h4>
        <v-btn
          v-for="exhibit in exhibits"
          :key="exhibit.pk"
          :href="exhibit.file"
          target="_blank"
        >
          {{ exhibit.name }}
        </v-btn>
        <v-divider />
      </template>

      <!-- Map -->
      <Map
        v-if="!hasCompleted && mapType === `single`"
        :map-information="selectedSurvey.map_info"
      />
      <MultiPointMap
        v-if="!hasCompleted && mapType === `multi`"
        :map-information="selectedSurvey.map_info"
      />

      <!-- Extra Text -->
      <template v-if="!hasCompleted && selectedSurvey.extra_text">
        <!-- eslint-disable-next-line vue/no-v-html -->
        <span v-html="selectedSurvey.extra_text" />
      </template>

      <!-- Survey -->
      <survey :survey="SurveyJSObject" />

      <!-- Messages -->
      <span
        v-if="hasCompleted"
        class="text-center"
      >
        <h2 v-if="showSurveyError">
          {{ $t("message.surveyError") }}
        </h2>
        <h2 v-else>
          {{ $t("message.thanks") }}
        </h2>
        <StakeholderChoice
          v-if="shouldShowSurveySelect"
          @set-has-completed="setHasCompleted"
        />
        <span v-else>
          {{ $t("message.redirect") }}
        </span>
      </span>
    </div>
    <LanguageSelect />
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import Map from "@/components/Survey/Map";
import MultiPointMap from "@/components/Survey/MultiPointMap";
import LanguageSelect from "@/components/Utilities/LanguageSelect";
import StakeholderChoice from "@/components/Survey/StakeholderChoice.vue";
import * as SurveyVue from "survey-vue";
import * as widgets from "surveyjs-widgets";
import showdown from "showdown";
let Survey = SurveyVue.Survey;

// IE polyfill for calling forEach() on NodeList
if (window.NodeList && !NodeList.prototype.forEach) {
  NodeList.prototype.forEach = Array.prototype.forEach;
}

Survey.cssType = "bootstrap";
widgets.prettycheckbox(SurveyVue);
widgets.bootstrapslider(SurveyVue);
widgets.sortablejs(SurveyVue);

import { toUpperCase, showBrandingForComponent, getBrandingImageFilePath } from "@/utils/dynamicBranding.js";

export default {
  name: "SurveyContainer",
  components: {
    Survey,
    Map,
    MultiPointMap,
    LanguageSelect,
    StakeholderChoice
  },
  data() {
    return {
      hasCompleted: false,
      showSurveyError: false,
      SurveyJSObject: new SurveyVue.Model({}),
      selectedSurvey: {},
      mapType: "none",
    };
  },
  computed: {
    ...mapState({
      surveys: state => state.survey.surveys,
      projects: state => state.projects.projects,
      programs: state => state.programs.programs,
      exhibits: state => state.exhibits.surveyExhibits,
      surveyError: state => state.auth.surveyError,
      coordinatesWithType: state => state.response.coordinatesWithType,
      coordinatesNoType: state => state.response.coordinates,
      currentStakeholderID: state => state.stakeholders.currentStakeholderID,
      contactID: state => state.stakeholders.contactID,
      comment_location: state => state.response.coordinates,
      viewedVirtualMeeting: state => state.survey.viewedVirtualMeeting
    }),
    shouldShowSurveySelect() {
      const programNameFromURL = this.$router.currentRoute.params.program_name;
      const surveyNameFromURL = this.$router.currentRoute.params.survey_name;
      if (this.programs.find(program => toUpperCase(program.name) === toUpperCase(programNameFromURL.replace(/_/g, " "))).view_multiple_surveys) {
        return this.surveys.find(survey => toUpperCase (survey.name) === toUpperCase(surveyNameFromURL.replace(/_/g, " "))).view_in_list
      }
      return false
    }
  },
  watch: {
    "$i18n.locale"() {
      this.SurveyJSObject.locale =
        this.$i18n.locale === "en" ? "default" : this.$i18n.locale;
    },
    hasCompleted() {
      if (!this.hasCompleted) {
        this.setSelectedSurvey()
        this.setSurveyJSObject()
        this.getSurveyExhibits(this.selectedSurvey);
      }
    }
  },
  created() {
    this.setSelectedSurvey()
    this.getSurveyExhibits(this.selectedSurvey);
    this.setSurveyJSObject()
    this.getProjects()
  },
  methods: {
    ...mapActions({
      emailAfterSurveyResponse: "stakeholders/emailAfterSurveyResponse",
      getSurveyExhibits: "exhibits/getSurveyExhibits",
      addResponse: "response/addResponse",
      getProjects: "projects/getProjects"
    }),
    submit_response() {
      let contactId;
      this.currentStakeholderID === null
        ? (contactId = this.contactID)
        : (contactId = this.currentStakeholderID);

      const commentLocation = this.mapType === "single" ? this.coordinatesNoType : this.coordinatesWithType
      const payload = {
        survey: this.selectedSurvey.pk,
        survey_taker_id: contactId,
        response_json: this.SurveyJSObject.data,
        comment_location: commentLocation,
        survey_metadata: this.selectedSurvey.survey_metadata.pk
      };
      this.addSurveyResponse(payload);
    },
    addSurveyResponse(payload) {
      this.addResponse(payload).then(() => {
        this.hasCompleted = true;
        this.emailAfterSurveyResponse({ stakeholder_pk: payload.survey_taker_id, email_template_pk: this.selectedSurvey.email_template });
      });
      if (!this.shouldShowSurveySelect) {
        let page = -1
        if (this.selectedSurvey.story_map_url && this.viewedVirtualMeeting) {
          page = -2
        }
        setTimeout(() => this.$router.go(page), 5000);
      }
    },
    setHasCompleted(value) {
      this.hasCompleted = value;
    },
    setSelectedSurvey() {
      let programName = this.$router.currentRoute.params.program_name.replace(/_/g, " ");
      let projectName = this.$router.currentRoute.params.project_name.replace(/_/g, " ");
      let surveyName = this.$router.currentRoute.params.survey_name.replace(/_/g, " ");
      let currentSurvey = this.surveys.find(survey => {
        if (toUpperCase(survey.survey_metadata.meta_data.dashboardFiltering.program) === toUpperCase(programName) &&
          toUpperCase(survey.survey_metadata.meta_data.dashboardFiltering.project) === toUpperCase(projectName) &&
          toUpperCase(survey.survey_metadata.meta_data.dashboardFiltering.survey) === toUpperCase(surveyName)) {
          return survey;
        }
      }
      );
      this.selectedSurvey = currentSurvey;
      this.mapType = currentSurvey.map_type;
    },
    setSurveyJSObject() {
      const survey = this.checkMobileViewport(this.selectedSurvey.survey_json)
      let model = new SurveyVue.Model(survey);
      const converter = new showdown.Converter();
      model.onComplete.add(() => {
        this.submit_response();
      });
      model.getAllQuestions().forEach(q => {
        if (q.getType() === "sortablelist") {
          q.emptyText = "<b> &nbsp; &nbsp; Move the options below to reflect the order of importance </b>"
        }
        if (q.getType() === "bootstrapslider") {
          q.value = (q.rangeMax + q.rangeMin) / 2
        }
      });

      model.onValidateQuestion.add(function(survey, options){
        if (options.name === "ThreeMostImportantToYou") {
          const answers = options.value.length
          if (!(answers === 3 || answers === 0)) {
            options.error = "Please select three values, or skip this question if you'd prefer";
          }
        }
      });

      model.onValueChanging.add(function(survey, options) {
        survey.getAllQuestions().forEach(q => {
          if (q.getType() === "sortablelist" && q.name === options.name) {
            if (options.oldValue !== options.value) {
              q.resultEl.el.childNodes.forEach((node, index) => {
                if (index > 0) {
                  const choice = q.choices.find(choice => {
                    return choice.value === node.dataset.value
                  })
                  node.innerHTML = `<div class="sjs-sortablejs-item" style="background-color:#1ab394;color:#fff;margin:5px;padding:10px;">${index}. ${choice.text}</div>`;
                  node.outerHTML = `<div data-value="${node.dataset.value}"><div class="sjs-sortablejs-item" style="background-color:#1ab394;color:#fff;margin:5px;padding:10px;">${index}. ${choice.text}</div></div>`;
                }
              })
              q.sourceEl.el.childNodes.forEach(node => {
                const choice = q.choices.find(choice => {
                  return choice.value === node.dataset.value
                })
                node.innerHTML = `<div class="sjs-sortablejs-item" style="background-color:#1ab394;color:#fff;margin:5px;padding:10px;">${choice.text}</div>`;
                node.outerHTML = `<div data-value="${node.dataset.value}"><div class="sjs-sortablejs-item" style="background-color:#1ab394;color:#fff;margin:5px;padding:10px;">${choice.text}</div></div>`;
              })
            }
          }
        })
      })

      // There use to be a method that would check for questions that were like "Please choose 3 values"
      // and the logic used to be handled here, but can be handled in validations property inside the
      // actual survey, please check Campbellton Corridor Transit Project survey for example.

      model.onTextMarkdown.add(function(model, options) {
        //  convert the mardown text to html
        var str = converter.makeHtml(options.text);
        //  remove root paragraphs <p></p>
        str = str.substring(3);
        str = str.substring(0, str.length - 4);
        //  set html
        options.html = str;
      });

      model.locale = this.$i18n.locale === "en" ? "default" : this.$i18n.locale;
      model.showCompletedPage = false;
      model.clearInvisibleValues = "none";
      model.onUpdateQuestionCssClasses.add(function(survey, options) {
        var classes = options.cssClasses;
        classes.mainRoot += ` ${options.question.name}`
        classes.root = `sq-root sq-root-${options.question.getType()}`;
        classes.title = "sq-title";
        classes.item = "sq-item";
        classes.label = "sq-label";

        if (options.question.isRequired) {
          classes.title = "sq-title sq-title-required";
          classes.root = `sq-root sq-root-${options.question.getType()} sq-root-required sq-name-${options.question.name}`;
        }
      });
      model.showQuestionNumbers = "off";
      this.SurveyJSObject = model;
    },
    checkMobileViewport(survey) {
      // This checks the current viewport settings and if its a mobile breakpoint
      // it will change all the column counts to one so that it renders better on mobile devices
      if (this.$vuetify.breakpoint.xs) {
        const questionList = survey["pages"][0]["elements"]
        survey["pages"][0]["elements"] = questionList.map(question => {
          question["colCount"] = 1
          return question
        })
      }
      return survey
    },
    showBranding() {
      return showBrandingForComponent(this.$router.currentRoute.params, this.programs, this.projects, this.surveys, 'SurveyContainer', this.hasCompleted)
    },
    getBrandingImage() {
      return getBrandingImageFilePath('SurveyContainer.')
    },
  }
};
</script>

<style>
/* Use for the ranking type of questions */
/* .sjs-sortablejs-source {
  display: none;
} */
.sjs-sortablejs-result .sjs-sortablejs-item {
  background-color: #0193CF !important;
}
.sjs-sortablejs-source .sjs-sortablejs-item {
  background-color: #F37521 !important;
}

/* Use to style the slider bar for bootstrapslider */
.slider.slider-horizontal {
  margin-top: 1%;
  margin-left: 10%;
  width: 80%;
}


@media (min-width: 1024px) {
  /* Specific question that would render strange without flex display*/
  .sq-name-GreaterInvestment .sq-item {
    display: flex !important;
  }

  /* Makes all the questions a bit more centered */
  .sv_qstn fieldset{
    display: block;
    margin: 1.0em auto;
    text-align: center;
  }
}

.pretty input:checked ~ .state.p-success label:after,
.pretty.p-toggle .state.p-success label:after {
  background-color: #1f69a1 !important;
}

/* Used for bootstrapslider question with 5 values */
.SupportLevel [aria-valuenow="1"] {
  background-image: linear-gradient(
    to bottom,
    #FF0000 0%,
    #FF0000 100%
  ) !important;
}

.SupportLevel [aria-valuenow="2"] {
  background-image: linear-gradient(
    to bottom,
    #ff6600  0%,
    #ff6600  100%
  ) !important;
}
.SupportLevel [aria-valuenow="3"] {
  background-image: linear-gradient(
    to bottom,
    #FFFF00  0%,
    #FFFF00  100%
  ) !important;
}
.SupportLevel [aria-valuenow="4"] {
  background-image: linear-gradient(
    to bottom,
    #66ff00 0%,
    #66ff00 100%
  ) !important;
}
.SupportLevel [aria-valuenow="5"] {
  background-image: linear-gradient(
    to bottom,
    #008000  0%,
    #008000  100%
  ) !important;
}

/* These can be re-used for bootstrapsliders when
they are 3 values, just put name of question in
front of them (example above) */

/*[aria-valuenow="1"] {
  background-image: linear-gradient(
    to bottom,
    #ff4444 0%,
    #ff4444 100%
  ) !important;
}
[aria-valuenow="2"] {
  background-image: linear-gradient(
    to bottom,
    #ffbb33 0%,
    #ffbb33 100%
  ) !important;
}
[aria-valuenow="3"] {
  background-image: linear-gradient(
    to bottom,
    #00c851 0%,
    #00c851 100%
  ) !important;
} */

@media (min-width: 360px) and (max-width: 1023px) {
  .gdotlogo {
    position: relative;
    float: center;
    left: auto;
    top: auto;
    width: auto;
  }
}
.modal-body {
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}

label {
  float: left;
}

.tooltip.in {
  opacity: 1;
}

.panel-footer {
  text-align: center;
  height: 100px;
}

.panel-heading {
  text-align: center;
}

.sv_qstn .sq-root {
  border: 1px solid gray;
  border-radius: 5px;
  padding: 20px;
  width: 100%;
}
.sv_q_radiogroup_inline {
  padding: 2%;
}
.sv_prev_btn,
.sv_next_btn {
  display: none !important;
}

.sv_row {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  word-wrap: break-word;
  background-color: #fff;
  background-clip: border-box;
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 0.25rem;
  padding: 0.5rem;
}

.sv_complete_btn {
  background-color: #1f69a1;
  border: none;
  color: white;
  padding: 10px 15px;
  text-align: center;
  text-decoration: none;
  display: inline-block;
  font-size: 16px;
}

.sv_container .sv_main {
  border: 0;
}

.modal-backdrop {
  height: 100% !important;
  width: 100% !important;
}

.survey-card {
  width: 50%;
  left: 25%;
  margin-top: 2%;
  margin-bottom: 2%;
}

.NameRecommendations .sq-item {
  text-align: left
}
.logo-sr20{
  width: 20%;
  position: fixed;
  top: 15%;
  left: 3.75%;
}
.logo-fdot{
  width: 20%;
  position: fixed;
  top: 15%;
  right: 3.75%;
}
@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  .v-application {
    display: block;
  }
}
</style>

<style scoped>
@media (min-width: 360px) and (max-width: 1024px) {
  .gdotlogo {
    position: relative;
    float: center;
    left: auto !important;
    top: auto !important;
    width: auto;
  }
  .mobile-card {
    width: 80% !important;
    left: 10% !important;
  }
      .mobile-logo {
    position: relative;
    left: auto !important;
    width: auto;
    margin-left: 15%;
    width: 70%;
    top: auto;
  }
  .hidden-logo {
    display: none;
  }
}

body {
  font-size: 3vw;
}

.survey-logo {
  width: 40%;
  margin-left: 30%;
}
.survey-scout-logo {
  width: 25%;
  margin-top: 2%;
  margin-left: 36.5%;
  margin-bottom: 2.5%;
}

</style>
